import * as React from "react";
import { AVAILABILITY_LABEL, DISABLED_AGENT_NOTE, agents as agentsApi, type Availability } from "@/api/agents";
import { useSession } from "@/auth/session";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { attempt } from "@/lib/form-errors";
import { cn } from "@/lib/utils";
import { availabilityBlocked, availabilityChoices, availabilitySource } from "./model";

/**
 * Set an agent's availability. A supervisor changing someone else's says
 * so in `source`, which is what the status log records; do-not-disturb on
 * the user holds them logged out, and then the menu says why instead of
 * offering values the server would refuse. A disabled user may only be
 * logged out, so that is the one value the menu leaves enabled.
 */
export function AvailabilityMenu({
  accountId,
  agentId,
  current,
  dndEnabled,
  userDisabled,
  onChanged,
  children,
}: {
  accountId: string;
  agentId: string;
  current: Availability | undefined;
  dndEnabled?: boolean;
  /** The user is disabled: only a log out is offered. */
  userDisabled?: boolean;
  onChanged: () => void;
  children: React.ReactNode;
}) {
  const ownerId = useSession((s) => s.session?.ownerId);
  const blocked = availabilityBlocked(dndEnabled);
  const choices = availabilityChoices(current, { dndEnabled, userDisabled });
  const set = (value: Availability) =>
    void attempt(`Set to ${AVAILABILITY_LABEL[value]}`, () => agentsApi.setStatus(accountId, agentId, value, availabilitySource(ownerId === agentId)), onChanged);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 text-xs">
        <DropdownMenuLabel className="text-2xs font-normal text-muted-foreground">Availability</DropdownMenuLabel>
        {!blocked && userDisabled && <div className="px-2 pb-1 text-2xs text-muted-foreground">{DISABLED_AGENT_NOTE}</div>}
        {blocked ? (
          <div className="px-2 py-1.5 text-2xs text-muted-foreground">{blocked}</div>
        ) : (
          choices.map((c) => (
            <DropdownMenuItem key={c.value} disabled={c.disabled} onSelect={() => set(c.value)} className="text-xs">
              <div>
                <div>{c.label}</div>
                <div className="text-2xs text-muted-foreground">{c.help}</div>
              </div>
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-2xs font-normal text-muted-foreground">What their phones are doing is derived from the calls they are on and cannot be set.</DropdownMenuLabel>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The compact mark beside a disabled user's name in an agent list. */
export function DisabledAgentTag({ className }: { className?: string }) {
  return (
    <span className={cn("ml-1.5 rounded-sm bg-muted px-1 text-2xs font-normal text-muted-foreground", className)} title={DISABLED_AGENT_NOTE}>
      disabled
    </span>
  );
}
